import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';

const NumericFiltersSection = ({ onUpdate, metricOptions, boostInput = false }) => {
  const [currentMetric, setCurrentMetric] = useState('');
  const [currentOperator, setCurrentOperator] = useState('');
  const [currentThreshold, setCurrentThreshold] = useState('');
  const [boost, setBoost] = useState(1);

  const operatorOptions = [
    { id: '<=', label: '<=' },
    { id: '>=', label: '>=' }
  ];

  const handleMetricChange = (event) => {
    setCurrentMetric(event.target.value);
    setCurrentOperator('>=');
    setCurrentThreshold('0');
  };

  const handleMetricAction = (actionType) => {
    if (!currentMetric || !currentOperator || !currentThreshold) return;

    const selectedMetric = metricOptions.find(m => m.id === currentMetric);
    let thresholdValue;

    if (selectedMetric.type === 'decimal' || selectedMetric.type === 'integer') {
      thresholdValue = parseFloat(currentThreshold);
      if (isNaN(thresholdValue)) return;
    }

    const updateData = {
      action: actionType,
      filter: 'numeric',
      field: currentMetric,
      value: thresholdValue,
      operator: currentOperator,
    };

    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }

    onUpdate(updateData);

    // Reset after adding
    setCurrentMetric('');
    setCurrentOperator('');
    setCurrentThreshold('');
  };

  const getThresholdStep = () => {
    if (!currentMetric) return '0.01';
    const selectedMetric = metricOptions.find(m => m.id === currentMetric);
    return selectedMetric?.type === 'decimal' ? '0.01' : '1';
  };

  const getThresholdLabel = () => {
    if (!currentMetric) return 'number';
    const selectedMetric = metricOptions.find(m => m.id === currentMetric);
    return selectedMetric?.type === 'decimal' ? 'decimal' : 'integer';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Metric</InputLabel>
          <Select
            value={currentMetric}
            label="Metric"
            onChange={handleMetricChange}
          >
            {metricOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={currentOperator}
            label="Operator"
            onChange={(e) => setCurrentOperator(e.target.value)}
          >
            {operatorOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={`Threshold (${getThresholdLabel()})`}
          type="number"
          value={currentThreshold}
          onChange={(e) => setCurrentThreshold(e.target.value)}
          inputProps={{
            step: getThresholdStep()
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleMetricAction('include')}
          disabled={!currentMetric || !currentOperator || !currentThreshold}
        >
          <AddIcon />
        </Button>

        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleMetricAction('boost')}
            disabled={!currentMetric || !currentOperator || !currentThreshold}
          >
            <TrendingUpIcon />
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          onClick={() => handleMetricAction('exclude')}
          disabled={!currentMetric || !currentOperator || !currentThreshold}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default NumericFiltersSection;

